import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiSave, FiLock, FiCalendar, FiMapPin, FiClock,
  FiBookOpen, FiSearch, FiX, FiUsers, FiUserPlus, FiEdit2
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { reuniaoCriancasService, bibliaService, congregacaoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './ReuniaoCriancas.css';

const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;
  return Math.floor((new Date() - new Date(dataNascimento)) / 31557600000);
};

const parseLocalDate = (iso) => {
  if (!iso) return null;
  const s = String(iso).substring(0, 10);
  const [ano, mes, dia] = s.split('-').map(Number);
  return new Date(ano, mes - 1, dia);
};

const ReuniaoCriancasDetalhes = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { canWriteModule } = useAuth();
  const podeEditar = canWriteModule('reuniao_criancas');

  const [reuniao, setReuniao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [livros, setLivros] = useState([]);

  const [presentes, setPresentes] = useState([]);
  const [criancasDisponiveis, setCriancasDisponiveis] = useState([]);
  const [busca, setBusca] = useState('');
  const [buscaAberta, setBuscaAberta] = useState(false);
  const buscaRef = useRef(null);
  const wrapRef = useRef(null);

  const [colabIrmas, setColabIrmas] = useState(0);
  const [colabIrmaos, setColabIrmaos] = useState(0);

  const [palavra, setPalavra] = useState({
    livro: '', capitulo: '', versoInicio: '', versoFim: '',
  });
  const [observacoes, setObservacoes] = useState('');

  const [editandoInfo, setEditandoInfo] = useState(false);
  const [editInfo, setEditInfo] = useState({ dataReuniao: '', congregacaoNome: '', horario: '' });
  const [congregacoes, setCongregacoes] = useState([]);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      const r = await reuniaoCriancasService.getById(id);
      setReuniao(r);
      setPresentes(r.presentes || []);
      setColabIrmas(r.colaboradorasPresentes || 0);
      setColabIrmaos(r.colaboradoresPresentes || 0);
      setPalavra({
        livro: r.livro || '',
        capitulo: r.capitulo || '',
        versoInicio: r.versoInicio || '',
        versoFim: r.versoFim || '',
      });
      setObservacoes(r.observacoes || '');
    } catch {
      toast.error('Erro ao carregar reuniao');
      navigate('/reuniao-criancas');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    carregar();
    bibliaService.listarLivros().then((r) => setLivros(Array.isArray(r) ? r : r.data || [])).catch(() => {});
    reuniaoCriancasService.criancasDisponiveis().then((r) => {
      setCriancasDisponiveis(Array.isArray(r) ? r : r.data || []);
    }).catch(() => {});
    congregacaoService.getAtivas().then((r) => {
      const nomes = (r?.data || []).map((c) => c.nome || c.nomeCompleto).filter(Boolean);
      setCongregacoes(nomes);
    }).catch(() => {});
  }, [carregar]);

  useEffect(() => {
    const handleClickFora = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setBuscaAberta(false);
      }
    };
    document.addEventListener('mousedown', handleClickFora);
    return () => document.removeEventListener('mousedown', handleClickFora);
  }, []);

  const livroAtual = useMemo(
    () => livros.find((l) => l.nome === palavra.livro),
    [livros, palavra.livro]
  );
  const maxCap = livroAtual?.capitulos || null;
  const maxVerso = livroAtual && palavra.capitulo
    ? livroAtual.versosPorCapitulo[parseInt(palavra.capitulo, 10) - 1] || null
    : null;

  const idsPresentes = useMemo(
    () => new Set(presentes.map((p) => p.irmandadeId || p.id)),
    [presentes]
  );

  const resultadosBusca = useMemo(() => {
    if (!busca.trim()) return [];
    const termo = busca.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return criancasDisponiveis
      .filter((c) => {
        if (idsPresentes.has(c.id)) return false;
        const nome = (c.nomeCompleto || c.nome || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        return nome.includes(termo);
      })
      .slice(0, 20);
  }, [busca, criancasDisponiveis, idsPresentes]);

  const contadores = useMemo(() => {
    let meninas = 0;
    let meninos = 0;
    presentes.forEach((p) => {
      const tp = (p.tipoPessoa || '').toLowerCase();
      if (tp === 'irma') meninas++;
      else meninos++;
    });
    return { meninas, meninos, total: presentes.length };
  }, [presentes]);

  const marcarPresenca = async (crianca) => {
    try {
      await reuniaoCriancasService.marcarPresenca(id, { irmandadeId: crianca.id });
      setPresentes((prev) => [...prev, {
        irmandadeId: crianca.id,
        nome: crianca.nomeCompleto || crianca.nome,
        dataNascimento: crianca.dataNascimento,
        tipoPessoa: crianca.tipoPessoa,
      }]);
      setBusca('');
      setBuscaAberta(false);
      buscaRef.current?.focus();
    } catch {
      toast.error('Erro ao marcar presenca');
    }
  };

  const removerPresenca = async (crianca) => {
    const cId = crianca.irmandadeId || crianca.id;
    try {
      await reuniaoCriancasService.removerPresenca(id, cId);
      setPresentes((prev) => prev.filter((p) => (p.irmandadeId || p.id) !== cId));
    } catch {
      toast.error('Erro ao remover presenca');
    }
  };

  const salvar = async () => {
    try {
      setSalvando(true);
      await reuniaoCriancasService.atualizar(id, {
        livro: palavra.livro || null,
        capitulo: palavra.capitulo ? parseInt(palavra.capitulo, 10) : null,
        versoInicio: palavra.versoInicio ? parseInt(palavra.versoInicio, 10) : null,
        versoFim: palavra.versoFim ? parseInt(palavra.versoFim, 10) : null,
        observacoes,
        colaboradorasPresentes: parseInt(colabIrmas, 10) || 0,
        colaboradoresPresentes: parseInt(colabIrmaos, 10) || 0,
      });
      toast.success('Dados salvos!');
    } catch {
      toast.error('Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const salvarInfoBasica = async () => {
    try {
      await reuniaoCriancasService.atualizar(id, {
        dataReuniao: editInfo.dataReuniao,
        congregacaoNome: editInfo.congregacaoNome,
        horario: editInfo.horario,
      });
      toast.success('Informacoes atualizadas!');
      setEditandoInfo(false);
      carregar();
    } catch {
      toast.error('Erro ao atualizar informacoes');
    }
  };

  const fechar = async () => {
    if (!window.confirm('Apos fechar, a reuniao nao podera mais ser editada. Confirma?')) return;
    try {
      await reuniaoCriancasService.fechar(id);
      toast.success('Reuniao fechada com sucesso');
      carregar();
    } catch {
      toast.error('Erro ao fechar reuniao');
    }
  };

  if (loading) return <div className="rc-page"><div className="rc-empty">Carregando...</div></div>;
  if (!reuniao) return null;

  const bloqueado = reuniao.fechado || !podeEditar;

  return (
    <div className="rc-page">
      <button className="rc-back" onClick={() => navigate('/reuniao-criancas')}>
        <FiArrowLeft /> Voltar
      </button>

      <header className="rc-page-header">
        <div>
          <h1>Reuniao de Criancas</h1>
          {!editandoInfo ? (
            <div className="rc-meta-info">
              <span>
                <FiCalendar /> {(() => {
                  const d = parseLocalDate(reuniao.dataReuniao);
                  return d ? d.toLocaleDateString('pt-BR') : '';
                })()}
              </span>
              <span><FiClock /> {reuniao.horario || '09:00'}</span>
              <span><FiMapPin /> {reuniao.congregacaoNome}</span>
              {!bloqueado && (
                <button
                  className="rc-btn-icone"
                  onClick={() => {
                    setEditInfo({
                      dataReuniao: String(reuniao.dataReuniao).substring(0, 10),
                      congregacaoNome: reuniao.congregacaoNome || '',
                      horario: reuniao.horario || '09:00',
                    });
                    setEditandoInfo(true);
                  }}
                  title="Editar informacoes"
                >
                  <FiEdit2 />
                </button>
              )}
            </div>
          ) : (
            <div className="rc-form-grid" style={{ marginTop: 8 }}>
              <label>
                <span>Data</span>
                <input type="date" value={editInfo.dataReuniao} onChange={(e) => setEditInfo({ ...editInfo, dataReuniao: e.target.value })} />
              </label>
              <label>
                <span>Congregacao</span>
                <select value={editInfo.congregacaoNome} onChange={(e) => setEditInfo({ ...editInfo, congregacaoNome: e.target.value })}>
                  <option value="">Selecione...</option>
                  {congregacoes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label>
                <span>Horario</span>
                <input type="time" value={editInfo.horario} onChange={(e) => setEditInfo({ ...editInfo, horario: e.target.value })} />
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <button className="rc-btn rc-btn-primary" onClick={salvarInfoBasica}><FiSave /> Salvar</button>
                <button className="rc-btn rc-btn-ghost" onClick={() => setEditandoInfo(false)}>Cancelar</button>
              </div>
            </div>
          )}
        </div>
        {reuniao.fechado && (
          <span className="rc-tag rc-tag-fechada"><FiLock size={12} /> Fechada</span>
        )}
      </header>

      {reuniao.fechado && (
        <div className="rc-aviso-bloqueio">
          Esta reuniao esta fechada e nao pode mais ser editada.
        </div>
      )}

      {/* ── Presenca ── */}
      <section className="rc-section">
        <h2><FiUsers /> Presenca das criancas</h2>
        <p className="rc-section-sub">
          Busque pelo nome da crianca e clique para marcar presenca. Clique no X para remover.
        </p>

        <div className="rc-contadores">
          <span className="rc-contador rc-contador--meninas">Meninas: {contadores.meninas}</span>
          <span className="rc-contador rc-contador--meninos">Meninos: {contadores.meninos}</span>
          <span className="rc-contador rc-contador--total">Total: {contadores.total}</span>
        </div>

        {!bloqueado && (
          <div className="rc-search-wrap" ref={wrapRef}>
            <FiSearch className="rc-search-icon" />
            <input
              ref={buscaRef}
              className="rc-search-input"
              type="text"
              placeholder="Buscar crianca pelo nome..."
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setBuscaAberta(true);
              }}
              onFocus={() => busca.trim() && setBuscaAberta(true)}
            />
            {buscaAberta && busca.trim() && (
              <div className="rc-search-results">
                {resultadosBusca.length === 0 ? (
                  <div className="rc-search-empty">Nenhuma crianca encontrada</div>
                ) : (
                  resultadosBusca.map((c) => {
                    const idade = calcularIdade(c.dataNascimento);
                    return (
                      <div
                        key={c.id}
                        className="rc-search-item"
                        onClick={() => marcarPresenca(c)}
                      >
                        <div className="rc-search-item-info">
                          <span className="rc-search-item-name">{c.nomeCompleto || c.nome}</span>
                          <span className="rc-search-item-detail">
                            {idade !== null ? `${idade} anos` : ''}
                            {c.congregacao ? ` · ${c.congregacao}` : ''}
                          </span>
                        </div>
                        <FiUserPlus style={{ color: '#f59e0b', flexShrink: 0 }} />
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}

        <div className="rc-presentes-grid">
          {presentes.length === 0 ? (
            <div className="rc-presentes-vazio">Nenhuma crianca registrada ainda</div>
          ) : (
            presentes.map((p) => {
              const cId = p.irmandadeId || p.id;
              const nome = p.nome || p.nomeCompleto || '';
              const idade = calcularIdade(p.dataNascimento);
              const isMenina = (p.tipoPessoa || '').toLowerCase() === 'irma';
              return (
                <div
                  key={cId}
                  className={`rc-presente-chip ${isMenina ? 'rc-presente-chip--menina' : 'rc-presente-chip--menino'}`}
                >
                  {nome}
                  {idade !== null && <span className="rc-chip-age">{idade}a</span>}
                  {!bloqueado && (
                    <button
                      className="rc-chip-remove"
                      onClick={() => removerPresenca(p)}
                      title="Remover"
                    >
                      <FiX />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* ── Colaboradores ── */}
      <section className="rc-section">
        <h2><FiUsers /> Colaboradores</h2>
        <p className="rc-section-sub">
          Quantidade de irmas e irmaos que colaboraram nesta reuniao.
        </p>

        <div className="rc-colab-grid">
          <label>
            <span>Irmas colaboradoras</span>
            <input
              type="number"
              min={0}
              value={colabIrmas}
              onChange={(e) => setColabIrmas(e.target.value)}
              onFocus={(e) => e.target.select()}
              disabled={bloqueado}
            />
          </label>
          <label>
            <span>Irmaos colaboradores</span>
            <input
              type="number"
              min={0}
              value={colabIrmaos}
              onChange={(e) => setColabIrmaos(e.target.value)}
              onFocus={(e) => e.target.select()}
              disabled={bloqueado}
            />
          </label>
        </div>
      </section>

      {/* ── Palavra do dia ── */}
      <section className="rc-section">
        <h2><FiBookOpen /> Palavra do dia</h2>
        <p className="rc-section-sub">
          Referencia biblica utilizada na reuniao.
        </p>

        <div className="rc-form-grid">
          <label>
            <span>Livro</span>
            <select
              value={palavra.livro}
              onChange={(e) => setPalavra({ ...palavra, livro: e.target.value, capitulo: '', versoInicio: '', versoFim: '' })}
              disabled={bloqueado}
            >
              <option value="">Selecione...</option>
              {livros.map((l) => <option key={l.nome} value={l.nome}>{l.nome}</option>)}
            </select>
          </label>

          <label>
            <span>Capitulo {maxCap ? `(1-${maxCap})` : ''}</span>
            <input
              type="number"
              min={1}
              max={maxCap || undefined}
              value={palavra.capitulo}
              onChange={(e) => setPalavra({ ...palavra, capitulo: e.target.value, versoInicio: '', versoFim: '' })}
              disabled={bloqueado || !palavra.livro}
            />
          </label>

          <label>
            <span>Do verso {maxVerso ? `(max ${maxVerso})` : ''}</span>
            <input
              type="number"
              min={1}
              max={maxVerso || undefined}
              value={palavra.versoInicio}
              onChange={(e) => setPalavra({ ...palavra, versoInicio: e.target.value })}
              disabled={bloqueado || !palavra.capitulo}
            />
          </label>

          <label>
            <span>Ao verso {maxVerso ? `(max ${maxVerso})` : ''}</span>
            <input
              type="number"
              min={palavra.versoInicio || 1}
              max={maxVerso || undefined}
              value={palavra.versoFim}
              onChange={(e) => setPalavra({ ...palavra, versoFim: e.target.value })}
              disabled={bloqueado || !palavra.capitulo}
            />
          </label>
        </div>

        <label className="rc-form-full">
          <span>Observacoes</span>
          <textarea
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            rows={3}
            disabled={bloqueado}
            placeholder="Ex: tema especial, visita do ministerio, evento, etc."
          />
        </label>
      </section>

      {/* ── Acoes ── */}
      {!bloqueado && (
        <div className="rc-action-bar">
          <button
            className="rc-btn rc-btn-primary"
            onClick={salvar}
            disabled={salvando}
          >
            <FiSave /> {salvando ? 'Salvando...' : 'Salvar dados'}
          </button>
          {presentes.length > 0 && (
            <button
              className="rc-btn rc-btn-ghost"
              onClick={fechar}
            >
              <FiLock /> Fechar reuniao
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ReuniaoCriancasDetalhes;
